// Components
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { TableColumnHeader } from '@Components/shared/tables/columns';
import TruncateText from '@Components/truncate-text/TruncateText';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Utils
import {
  UserListColumns,
  UserListFilter,
  userListColumns,
} from '@utils/tables/users/userListFilter';

import { handleCopyClick } from '@Components/shared/copyText/CoptyText';
import { useSnackbar } from 'notistack';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { UserItemDto } from '@contracts/api/dto/users/UserDto';
import { Filterable } from '@contracts/common/traits/Filterable';
import { ProvidersTableGames, ProvidersTableGamesKeys } from '@Modal/reducers/providers/types';

type HeaderOptions = {
  filter: Filterable<UserListFilter>;
};

type Columns = TableColumnDescriptionArray<UserItemDto, HeaderOptions, typeof userListColumns>;

type Column = TableColumnDescription<UserItemDto, HeaderOptions, UserListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useUserTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { enqueueSnackbar } = useSnackbar();
  return useMemo(
    (): Column[] =>
      [
        {
          name: 'user.id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>{row?.id && <TruncateText text={row?.id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'user.email',
          title: 'Email',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.email}</Box>,
        },
        {
          name: 'user.providerId',
          title: 'Provider ID',
          sx: { minWidth: '100px', maxWidth: '130px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box
              sx={{
                ...ellipsis,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {row?.id}{' '}
              <ContentCopyIcon
                onClick={(event) => handleCopyClick(event, row?.providerId || '', enqueueSnackbar)}
              />
            </Box>
          ),
        },
        {
          name: 'user.games',
          title: 'Games',
          sx: { minWidth: '100px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.games
                ?.map((game) => ProvidersTableGames[game as ProvidersTableGamesKeys])
                .join(', ')}
            </>
          ),
        },
        // {row?.games
        //   ?.map((game) => ProvidersTableGames[game as ProvidersTableGamesKeys])
        //   .join(', ')}
        {
          name: 'user.providerRgs.rgsId',
          title: 'Provider RGS',
          sx: { minWidth: '100px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.providerRgs?.map((rgs) => rgs.rgsId).join(', ')}</Box>
          ),
        },
        {
          name: 'user.roleIds',
          title: 'Role IDs',
          sx: { minWidth: '250px', maxWidth: '250px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis, display: 'flex', flexDirection: 'column' }}>
              {row?.roleIds?.map((id) => id)}
            </Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
